import CompanyDocumentReminders from '../components/CompanyDocumentReminders';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { dashboardApi, bookingApi, maintenanceApi, vehicleCostApi, letterApi, tireApi, dunningApi, cleaningApi } from '../services/api';
import { 
  Car, Users, Calendar, CurrencyEur, ArrowRight, 
  Clock, CheckCircle, Warning, ChartLineUp, SignIn, SignOut, Phone, Wrench, TrendUp, TrendDown, Sparkle, EnvelopeSimple
} from '@phosphor-icons/react';
import { BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const euro = value => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));
const currentMonth = () => {
  const parts = new Intl.DateTimeFormat('en', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit' }).formatToParts(new Date());
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}`;
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [revenueMonth, setRevenueMonth] = useState(currentMonth);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState('');
  const [revenueRetry, setRevenueRetry] = useState(0);
  useEffect(() => {
    let active = true;
    setRevenueLoading(true); setRevenueError('');
    dashboardApi.getStats(revenueMonth).then(({ data }) => {
      if (active) setStats(data);
    }).catch(() => {
      if (active) setRevenueError('Umsatz konnte nicht geladen werden. Bitte erneut versuchen.');
    }).finally(() => { if (active) setRevenueLoading(false); });
    return () => { active = false; };
  }, [revenueMonth, revenueRetry]);
  const revenueValue = key => revenueLoading ? '…' : revenueError ? '—' : euro(stats?.revenue?.[key]);
  const revenueLabel = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' }).format(new Date(`${revenueMonth}-01T12:00:00`));
  const [charts, setCharts] = useState(null);
  const [recentBookings, setRecentBookings] = useState([]);
  const [quick, setQuick] = useState(null);
  const [upcomingMaint, setUpcomingMaint] = useState([]);
  const [tireReminders, setTireReminders] = useState([]);
  const [dunning, setDunning] = useState(null);
  const [topVehicles, setTopVehicles] = useState(null);
  const [autoLetters, setAutoLetters] = useState({ total: 0, auto: 0, recent: [] });
  const [cleaning, setCleaning] = useState({ in_cleaning: 0, overdue: 0, vehicles: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const year = new Date().getFullYear();
      const [chartsRes, bookingsRes, quickRes, maintRes, costRes, draftLettersRes, tireRes, dunningRes, cleaningRes] = await Promise.all([
        dashboardApi.getCharts(6),
        bookingApi.getAll({ limit: 5 }),
        dashboardApi.getQuickActions(),
        maintenanceApi.upcoming(30),
        vehicleCostApi.get(`${year}-01`, `${year}-12`, null).catch(() => ({ data: null })),
        letterApi.list({ status: 'draft' }).catch(() => ({ data: [] })),
        tireApi.reminders().catch(() => ({ data: { items: [] } })),
        dunningApi.stats().catch(() => ({ data: null })),
        cleaningApi.dashboardStats().catch(() => ({ data: { in_cleaning: 0, overdue: 0, vehicles: [] } })),
      ]);
      setCharts(chartsRes.data);
      setRecentBookings(bookingsRes.data.slice(0, 5));
      setQuick(quickRes.data);
      setUpcomingMaint(maintRes.data || []);
      setTireReminders(tireRes.data?.items || []);
      setDunning(dunningRes.data || null);
      setCleaning(cleaningRes.data || { in_cleaning: 0, overdue: 0, vehicles: [] });
      // Draft-Briefe zusammenfassen (davon: auto-generierte)
      const drafts = draftLettersRes.data || [];
      const autoDrafts = drafts.filter((l) => !!l.auto_generated_from);
      setAutoLetters({
        total: drafts.length,
        auto: autoDrafts.length,
        recent: autoDrafts.slice(0, 3),
      });

      // Compute Top 3 winners and losers from cost analysis
      if (costRes.data?.vehicles) {
        const withActivity = costRes.data.vehicles.filter(
          (v) => (v.revenue || 0) > 0 || (v.total_cost || 0) > 0
        );
        const winners = [...withActivity]
          .filter((v) => v.profit > 0)
          .sort((a, b) => b.profit - a.profit)
          .slice(0, 3);
        const losers = [...withActivity]
          .filter((v) => v.profit < 0 || (v.total_cost > 0 && v.revenue === 0))
          .sort((a, b) => a.profit - b.profit)
          .slice(0, 3);
        setTopVehicles({ winners, losers, year });
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'badge-warning',
      active: 'badge-success',
      completed: 'badge-neutral',
      cancelled: 'badge-error'
    };
    const labels = {
      confirmed: 'Bestätigt',
      active: 'Aktiv',
      completed: 'Abgeschlossen',
      cancelled: 'Storniert'
    };
    return <span className={`badge ${styles[status] || 'badge-neutral'}`}>{labels[status] || status}</span>;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload) return null;
    return (
      <div className="bg-white border border-[#E2E4E9] rounded-sm p-3 shadow-lg">
        <p className="font-semibold text-[#111418] text-sm mb-1">{label}</p>
        {payload.map((entry, i) => {
          let val = entry.value;
          const name = entry.name;
          if (name === 'Auslastung') val = `${val}%`;
          else if (name === 'Bezahlt' || name === 'Offen' || name === 'Umsatz') val = `€${val.toFixed(2)}`;
          else val = val;
          return (
            <p key={i} className="text-xs" style={{ color: entry.color }}>
              {name}: {val}
            </p>
          );
        })}
      </div>
    );
  };

  if (loading || (!stats && revenueLoading)) {
    return (
      <div className="p-6 space-y-6">
        <div className="h-8 w-48 skeleton rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="stat-card h-32 skeleton" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card-swiss h-64 skeleton" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="dashboard-page">
      <CompanyDocumentReminders />
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111418]">Dashboard</h1>
          <p className="text-[#525866] mt-1">Übersicht Ihrer Autovermietung</p>
        </div>
        <Link 
          to="/bookings/new" 
          className="btn-primary px-6 py-3 flex items-center gap-2"
          data-testid="new-booking-btn"
        >
          Neue Buchung
          <ArrowRight weight="bold" size={18} />
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <label htmlFor="dashboard-revenue-month" className="text-sm font-semibold">Umsatzmonat</label>
        <input id="dashboard-revenue-month" type="month" className="input-swiss w-44" value={revenueMonth}
          onChange={event => { if (/^[0-9]{4}-(0[1-9]|1[0-2])$/.test(event.target.value)) setRevenueMonth(event.target.value); }} />
        {revenueMonth !== currentMonth() && <button type="button" className="text-sm underline" onClick={() => setRevenueMonth(currentMonth())}>Aktueller Monat</button>}
        <span className="text-xs text-[#525866]">Bezahlte Rechnungen nach Erstellungsmonat</span>
      </div>
      {revenueError && <div role="alert" className="text-sm text-[#E60000]">{revenueError} <button type="button" className="underline" onClick={() => setRevenueRetry(value => value + 1)}>Erneut versuchen</button></div>}
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        <Link to="/vehicles" aria-label="Fahrzeuge öffnen" className="stat-card animate-fade-in block cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E60000] focus-visible:ring-offset-2" style={{ animationDelay: '0ms' }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="overline mb-1">Fahrzeuge</p>
              <p className="stat-value">{stats?.vehicles?.total || 0}</p>
              <p className="stat-label">
                <span className="text-[#009951]">{stats?.vehicles?.available || 0} verfügbar</span>
                {' · '}
                <span className="text-[#E60000]">{stats?.vehicles?.rented || 0} vermietet</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
              <Car weight="fill" className="w-6 h-6 text-[#E60000]" />
            </div>
          </div>
        </Link>

        <Link to="/customers" aria-label="Kunden öffnen" className="stat-card animate-fade-in block cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E60000] focus-visible:ring-offset-2" style={{ animationDelay: '50ms' }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="overline mb-1">Kunden</p>
              <p className="stat-value">{stats?.customers || 0}</p>
              <p className="stat-label">Registrierte Kunden</p>
            </div>
            <div className="w-12 h-12 bg-[#0A0A0A]/10 rounded-sm flex items-center justify-center">
              <Users weight="fill" className="w-6 h-6 text-[#0A0A0A]" />
            </div>
          </div>
        </Link>

        <Link to="/bookings" aria-label="Buchungen öffnen" className="stat-card animate-fade-in block cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E60000] focus-visible:ring-offset-2" style={{ animationDelay: '100ms' }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="overline mb-1">Buchungen</p>
              <p className="stat-value">{stats?.bookings?.active || 0}</p>
              <p className="stat-label">
                <span className="text-[#FFB800]">{stats?.bookings?.confirmed || 0} ausstehend</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-[#009951]/10 rounded-sm flex items-center justify-center">
              <Calendar weight="fill" className="w-6 h-6 text-[#009951]" />
            </div>
          </div>
        </Link>

        <Link to="/invoices" aria-label="Umsatz und Finanzen öffnen" className="stat-card animate-fade-in block cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E60000] focus-visible:ring-offset-2" style={{ animationDelay: '150ms' }}>
          <div className="flex items-start justify-between">
            <div>
              <p className="overline mb-1">Umsatz (Monat)</p>
              <p className="text-xs text-[#525866] mb-1" data-testid="revenue-month-label">{revenueLabel}</p>
              <p className="stat-value" data-testid="revenue-monthly" aria-live="polite">{revenueValue('monthly')}</p>
              <p className="stat-label">
                <span className="text-[#FFB800]">{revenueValue('pending')} offen</span>
              </p>
            </div>
            <div className="w-12 h-12 bg-[#009951]/10 rounded-sm flex items-center justify-center">
              <ChartLineUp weight="fill" className="w-6 h-6 text-[#009951]" />
            </div>
          </div>
        </Link>
        <Link to="/invoices" aria-label="Gesamtumsatz öffnen" className="stat-card block cursor-pointer transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E60000]">
          <p className="overline mb-1">Gesamtumsatz</p>
          <p className="text-xs text-[#525866] mb-1">Alle Monate · bezahlt</p>
          <p className="stat-value" data-testid="revenue-total" aria-live="polite">{revenueValue('total')}</p>
          <p className="stat-label"><span className="text-[#FFB800]">{revenueValue('total_pending')} offen</span></p>
        </Link>
      </div>

      {/* Charts Row */}
      {charts && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Chart */}
          <div className="card-swiss p-4" data-testid="revenue-chart">
            <h3 className="font-bold text-[#111418] mb-1">Umsatz</h3>
            <p className="text-xs text-[#8A94A6] mb-4">Letzte 6 Monate</p>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.revenue} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A94A6' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8A94A6' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="bezahlt" name="Bezahlt" fill="#009951" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="offen" name="Offen" fill="#FFB800" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Utilization Chart */}
          <div className="card-swiss p-4" data-testid="utilization-chart">
            <h3 className="font-bold text-[#111418] mb-1">Flottenauslastung</h3>
            <p className="text-xs text-[#8A94A6] mb-4">Prozent der gebuchten Fahrzeugtage</p>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={charts.utilization} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A94A6' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8A94A6' }} unit="%" domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area type="monotone" dataKey="auslastung" name="Auslastung" stroke="#E60000" fill="#E60000" fillOpacity={0.1} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bookings Chart */}
          <div className="card-swiss p-4" data-testid="bookings-chart">
            <h3 className="font-bold text-[#111418] mb-1">Buchungen</h3>
            <p className="text-xs text-[#8A94A6] mb-4">Neue Buchungen pro Monat</p>
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={charts.bookings} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E4E9" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8A94A6' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#8A94A6' }} allowDecimals={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="buchungen" name="Neue Buchungen" stroke="#0A0A0A" strokeWidth={2} dot={{ fill: '#0A0A0A', r: 4 }} />
                  <Line type="monotone" dataKey="aktiv" name="Aktiv" stroke="#009951" strokeWidth={2} dot={{ fill: '#009951', r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Top / Flop Vehicles (Profit Ranking) */}
      {topVehicles && (topVehicles.winners.length > 0 || topVehicles.losers.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="top-vehicles-section">
          {/* Top Profit */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#009951]/10 rounded-sm flex items-center justify-center">
                  <TrendUp weight="fill" size={16} className="text-[#009951]" />
                </div>
                <div>
                  <h2 className="text-base font-bold tracking-tight">Top 3 Profit-Fahrzeuge</h2>
                  <p className="text-xs text-[#8A94A6]">Höchster Gewinn {topVehicles.year}</p>
                </div>
              </div>
              <Link to="/reports" className="text-xs text-[#525866] hover:text-[#E60000]">Bericht öffnen</Link>
            </div>
            <div className="divide-y divide-[#E2E4E9]" data-testid="top-winners-list">
              {topVehicles.winners.length === 0 ? (
                <div className="p-4 text-sm text-[#8A94A6]">Noch keine profitablen Fahrzeuge im laufenden Jahr.</div>
              ) : (
                topVehicles.winners.map((v, idx) => (
                  <div key={v.vehicle_id} className="p-3 flex items-center gap-3 hover:bg-[#FAFAFB]" data-testid={`top-winner-${idx}`}>
                    <div className="w-7 h-7 rounded-full bg-[#009951]/10 text-[#009951] text-xs font-bold flex items-center justify-center shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111418] truncate">{v.brand_model || '—'}</p>
                      <p className="text-xs text-[#8A94A6] font-mono">{v.license_plate}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#009951] tabular-nums">
                        +{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v.profit)}
                      </p>
                      <p className="text-xs text-[#8A94A6] tabular-nums">
                        Umsatz {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v.revenue)}
                        {' · '}{v.cost_ratio_pct}%
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Top Loss */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
                  <TrendDown weight="fill" size={16} className="text-[#E60000]" />
                </div>
                <div>
                  <h2 className="text-base font-bold tracking-tight">Top 3 Verlust-Fahrzeuge</h2>
                  <p className="text-xs text-[#8A94A6]">Höchster Verlust {topVehicles.year}</p>
                </div>
              </div>
              <Link to="/reports" className="text-xs text-[#525866] hover:text-[#E60000]">Bericht öffnen</Link>
            </div>
            <div className="divide-y divide-[#E2E4E9]" data-testid="top-losers-list">
              {topVehicles.losers.length === 0 ? (
                <div className="p-4 text-sm text-[#009951]">Keine Verlust-Fahrzeuge — top!</div>
              ) : (
                topVehicles.losers.map((v, idx) => (
                  <div key={v.vehicle_id} className="p-3 flex items-center gap-3 hover:bg-[#FAFAFB]" data-testid={`top-loser-${idx}`}>
                    <div className="w-7 h-7 rounded-full bg-[#E60000]/10 text-[#E60000] text-xs font-bold flex items-center justify-center shrink-0">
                      {idx + 1}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111418] truncate">{v.brand_model || '—'}</p>
                      <p className="text-xs text-[#8A94A6] font-mono">{v.license_plate}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-[#E60000] tabular-nums">
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v.profit)}
                      </p>
                      <p className="text-xs text-[#8A94A6] tabular-nums">
                        Kosten {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v.total_cost)}
                        {' · '}{v.cost_ratio_pct}%
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Alerts Section */}
      {(stats?.upcoming_returns > 0 || stats?.overdue?.count > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stats?.overdue?.count > 0 && (
            <Link to="/invoices" className="card-swiss p-4 border-l-4 border-l-[#E60000] hover:shadow-md transition-shadow" data-testid="overdue-alert">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
                  <Warning weight="fill" className="w-5 h-5 text-[#E60000]" />
                </div>
                <div>
                  <p className="font-bold text-[#E60000]">{stats.overdue.count} überfällige Rechnungen</p>
                  <p className="text-sm text-[#525866]">€{stats.overdue.total.toFixed(2)} offen</p>
                </div>
              </div>
            </Link>
          )}

          {stats?.upcoming_returns > 0 && (
            <Link to="/bookings" className="card-swiss p-4 border-l-4 border-l-[#FFB800] hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#FFB800]/10 rounded-sm flex items-center justify-center">
                  <Clock weight="fill" className="w-5 h-5 text-[#FFB800]" />
                </div>
                <div>
                  <p className="font-semibold text-[#111418]">{stats.upcoming_returns} Rückgaben</p>
                  <p className="text-sm text-[#525866]">in den nächsten 7 Tagen</p>
                </div>
              </div>
            </Link>
          )}

          {stats?.deposits?.count > 0 && (
            <Link to="/invoices" className="card-swiss p-4 border-l-4 border-l-[#009951] hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#009951]/10 rounded-sm flex items-center justify-center">
                  <CheckCircle weight="fill" className="w-5 h-5 text-[#009951]" />
                </div>
                <div>
                  <p className="font-semibold text-[#111418]">{stats.deposits.count} Kautionen</p>
                  <p className="text-sm text-[#525866]">€{stats.deposits.total.toFixed(2)} hinterlegt</p>
                </div>
              </div>
            </Link>
          )}

          {cleaning.in_cleaning > 0 && (
            <Link
              to="/vehicles?status=cleaning"
              className={`card-swiss p-4 border-l-4 hover:shadow-md transition-shadow ${cleaning.overdue > 0 ? 'border-l-[#E60000]' : 'border-l-[#3B82F6]'}`}
              data-testid="cleaning-tile"
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${cleaning.overdue > 0 ? 'bg-[#E60000]/10' : 'bg-blue-100'}`}>
                  <Sparkle weight="fill" className={`w-5 h-5 ${cleaning.overdue > 0 ? 'text-[#E60000]' : 'text-blue-700'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[#111418]">
                    {cleaning.in_cleaning} in Reinigung
                    {cleaning.overdue > 0 && <span className="ml-2 text-[#E60000] font-bold">· {cleaning.overdue} überfällig</span>}
                  </p>
                  <p className="text-sm text-[#525866]">
                    {cleaning.overdue > 0
                      ? `Länger als ${cleaning.overdue_hours || 24}h offen`
                      : 'Checkliste läuft'}
                  </p>
                </div>
              </div>
            </Link>
          )}
        </div>
      )}

      {/* Briefe warten auf Prüfung */}
      {autoLetters.total > 0 && (
        <Link
          to="/letters?status=draft"
          className="block card-swiss p-4 border-l-4 border-l-[#B8860B] hover:border-l-[#E60000] transition-colors"
          data-testid="draft-letters-card"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-sm bg-[#FFF7E6] flex items-center justify-center">
                <Sparkle weight="fill" size={20} className="text-[#B8860B]" />
              </div>
              <div>
                <p className="font-bold text-[#111418]" data-testid="draft-letters-count">
                  {autoLetters.total} Brief{autoLetters.total !== 1 ? 'e' : ''} warten auf Prüfung
                </p>
                <p className="text-xs text-[#525866] mt-0.5">
                  {autoLetters.auto > 0
                    ? `${autoLetters.auto} automatisch generiert · Bitte prüfen und ggf. versenden`
                    : 'Entwürfe im System — bitte prüfen und ggf. versenden'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-[#E60000] font-semibold text-sm">
              Zu den Briefen
              <ArrowRight size={14} weight="bold" />
            </div>
          </div>
          {autoLetters.recent.length > 0 && (
            <div className="mt-3 pt-3 border-t border-[#F0F1F3] space-y-1">
              {autoLetters.recent.map((l) => (
                <div key={l.id} className="flex items-center gap-2 text-xs text-[#525866]">
                  <EnvelopeSimple size={12} weight="bold" />
                  <span className="font-mono">{l.letter_number}</span>
                  <span className="truncate">· {l.subject}</span>
                  <span className="text-[#B8860B] shrink-0">· {l.recipient?.name || '—'}</span>
                </div>
              ))}
            </div>
          )}
        </Link>
      )}

      {/* Quick Actions: Check-In / Check-Out heute */}
      {quick && (quick.pending_checkins.length > 0 || quick.pending_checkouts_today.length > 0 || quick.overdue_checkouts.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="quick-actions-section">
          {/* Check-Ins */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#009951]/10 rounded-sm flex items-center justify-center">
                  <SignIn weight="fill" size={16} className="text-[#009951]" />
                </div>
                <h2 className="text-base font-bold tracking-tight">Heute Check-In ({quick.pending_checkins.length})</h2>
              </div>
              <Link to="/bookings?status=confirmed" className="text-xs text-[#525866] hover:text-[#E60000]">Alle</Link>
            </div>
            <div className="divide-y divide-[#E2E4E9]" data-testid="checkin-list">
              {quick.pending_checkins.length === 0 ? (
                <div className="p-4 text-sm text-[#8A94A6]">Keine offenen Check-Ins.</div>
              ) : (
                quick.pending_checkins.slice(0, 5).map((b) => (
                  <div key={b.id} className="p-3 flex items-center gap-3 hover:bg-[#FAFAFB]" data-testid={`checkin-row-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111418] truncate">{b.customer.name}</p>
                      <p className="text-xs text-[#525866] truncate">
                        {b.vehicle.label} <span className="font-mono">{b.vehicle.license_plate}</span>
                        {b.booking_number && <span className="ml-1 text-[#8A94A6]">• {b.booking_number}</span>}
                      </p>
                      <p className="text-xs text-[#8A94A6]">Start: {new Date(b.start_date).toLocaleDateString('de-DE')}</p>
                    </div>
                    {b.customer.phone && (
                      <a href={`tel:${b.customer.phone}`} className="p-2 rounded hover:bg-[#F1F2F4] text-[#525866]" title={b.customer.phone}>
                        <Phone weight="bold" size={14} />
                      </a>
                    )}
                    <Link
                      to={`/bookings/${b.id}?action=checkin`}
                      className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1"
                      data-testid={`quick-checkin-${b.id}`}
                    >
                      <SignIn weight="bold" size={12} /> Check-In
                    </Link>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Check-Outs */}
          <div className="card-swiss">
            <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-[#FFB800]/10 rounded-sm flex items-center justify-center">
                  <SignOut weight="fill" size={16} className="text-[#FFB800]" />
                </div>
                <h2 className="text-base font-bold tracking-tight">
                  Heute Check-Out ({quick.pending_checkouts_today.length})
                  {quick.overdue_checkouts.length > 0 && (
                    <span className="ml-2 text-xs text-[#E60000]">+ {quick.overdue_checkouts.length} überfällig</span>
                  )}
                </h2>
              </div>
              <Link to="/bookings?status=active" className="text-xs text-[#525866] hover:text-[#E60000]">Alle</Link>
            </div>
            <div className="divide-y divide-[#E2E4E9]" data-testid="checkout-list">
              {/* Overdue first (urgent) */}
              {quick.overdue_checkouts.slice(0, 5).map((b) => (
                <div key={b.id} className="p-3 flex items-center gap-3 bg-[#FFF5F5]" data-testid={`checkout-overdue-${b.id}`}>
                  <Warning weight="fill" size={16} className="text-[#E60000] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#111418] truncate">{b.customer.name}</p>
                    <p className="text-xs text-[#525866] truncate">
                      {b.vehicle.label} <span className="font-mono">{b.vehicle.license_plate}</span>
                    </p>
                    <p className="text-xs text-[#E60000] font-medium">Überfällig seit {new Date(b.end_date).toLocaleDateString('de-DE')}</p>
                  </div>
                  {b.customer.phone && (
                    <a href={`tel:${b.customer.phone}`} className="p-2 rounded hover:bg-[#F1F2F4] text-[#525866]" title={b.customer.phone}>
                      <Phone weight="bold" size={14} />
                    </a>
                  )}
                  <Link
                    to={`/bookings/${b.id}?action=checkout`}
                    className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1"
                    data-testid={`quick-checkout-${b.id}`}
                  >
                    <SignOut weight="bold" size={12} /> Check-Out
                  </Link>
                </div>
              ))}

              {/* Today */}
              {quick.pending_checkouts_today.length === 0 && quick.overdue_checkouts.length === 0 ? (
                <div className="p-4 text-sm text-[#8A94A6]">Keine offenen Check-Outs.</div>
              ) : (
                quick.pending_checkouts_today.slice(0, 5).map((b) => (
                  <div key={b.id} className="p-3 flex items-center gap-3 hover:bg-[#FAFAFB]" data-testid={`checkout-today-${b.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111418] truncate">{b.customer.name}</p>
                      <p className="text-xs text-[#525866] truncate">
                        {b.vehicle.label} <span className="font-mono">{b.vehicle.license_plate}</span>
                        {b.booking_number && <span className="ml-1 text-[#8A94A6]">• {b.booking_number}</span>}
                      </p>
                      <p className="text-xs text-[#8A94A6]">Rückgabe: heute</p>
                    </div>
                    {b.customer.phone && (
                      <a href={`tel:${b.customer.phone}`} className="p-2 rounded hover:bg-[#F1F2F4] text-[#525866]" title={b.customer.phone}>
                        <Phone weight="bold" size={14} />
                      </a>
                    )}
                    <Link
                      to={`/bookings/${b.id}?action=checkout`}
                      className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1"
                      data-testid={`quick-checkout-${b.id}`}
                    >
                      <SignOut weight="bold" size={12} /> Check-Out
                    </Link>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Maintenance Reminders */}
      {upcomingMaint.length > 0 && (
        <div className="card-swiss" data-testid="maintenance-reminders">
          <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#E60000]/10 rounded-sm flex items-center justify-center">
                <Wrench weight="fill" size={16} className="text-[#E60000]" />
              </div>
              <h2 className="text-base font-bold tracking-tight">
                Wartungen bald fällig ({upcomingMaint.length})
              </h2>
            </div>
            <Link to="/maintenance" className="text-xs text-[#525866] hover:text-[#E60000]">Alle ansehen</Link>
          </div>
          <div className="divide-y divide-[#E2E4E9]">
            {upcomingMaint.slice(0, 6).map((m) => {
              const days = m.next_due_at ? Math.floor((new Date(m.next_due_at) - new Date()) / (1000 * 60 * 60 * 24)) : null;
              return (
                <Link
                  key={m.id}
                  to={`/maintenance`}
                  className={`flex items-center gap-3 p-3 hover:bg-[#FAFAFB] ${m.is_overdue ? 'bg-[#FFF5F5]' : ''}`}
                  data-testid={`maint-reminder-${m.id}`}
                >
                  {m.is_overdue ? (
                    <Warning weight="fill" size={16} className="text-[#E60000] shrink-0" />
                  ) : (
                    <Calendar weight="bold" size={16} className="text-[#FFB800] shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#111418] truncate">
                      {m.vehicle ? `${m.vehicle.brand} ${m.vehicle.model}` : '—'}
                      <span className="ml-2 text-xs text-[#525866] font-mono">{m.vehicle?.license_plate}</span>
                    </p>
                    <p className="text-xs text-[#525866] truncate">
                      {m.title || ({
                        tuev: 'TÜV / HU', inspektion: 'Inspektion', reifenwechsel: 'Reifenwechsel',
                        oelwechsel: 'Ölwechsel', reparatur: 'Reparatur', versicherung: 'Versicherung', sonstige: 'Sonstige'
                      })[m.type]}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-xs font-semibold ${m.is_overdue ? 'text-[#E60000]' : (days <= 7 ? 'text-[#FFB800]' : 'text-[#525866]')}`}>
                      {m.is_overdue ? `${Math.abs(days)} Tage überfällig` : `in ${days} Tagen`}
                    </p>
                    <p className="text-xs text-[#8A94A6]">{new Date(m.next_due_at).toLocaleDateString('de-DE')}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      {/* Dunning summary */}
      {dunning && dunning.total_overdue_invoices > 0 && (
        <Link to="/dunning" className="block card-swiss hover:shadow-md transition" data-testid="dunning-dashboard-card">
          <div className="p-4 border-l-4 border-[#E60000] flex items-center gap-4">
            <div className="w-10 h-10 bg-[#E60000]/10 rounded flex items-center justify-center shrink-0">
              <Warning weight="fill" size={20} className="text-[#E60000]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-[#111418]">
                {dunning.total_overdue_invoices} überfällige Rechnung{dunning.total_overdue_invoices !== 1 ? 'en' : ''} · offen {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(dunning.total_open_amount || 0)}
              </p>
              <p className="text-xs text-[#525866] mt-0.5">
                {dunning.eligible_reminders > 0 ? `${dunning.eligible_reminders} Mahnung${dunning.eligible_reminders !== 1 ? 'en' : ''} zum Versand bereit` : 'Alle Mahnungen aktuell'}
              </p>
            </div>
            <span className="text-xs text-[#E60000] font-semibold shrink-0">Zum Mahnwesen →</span>
          </div>
        </Link>
      )}

      {/* Tire Change Reminders */}
      {tireReminders.length > 0 && (
        <div className="card-swiss" data-testid="tire-reminders-dashboard">
          <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-[#7C3AED]/10 rounded-sm flex items-center justify-center">
                <Wrench weight="fill" size={16} className="text-[#7C3AED]" />
              </div>
              <h2 className="text-base font-bold tracking-tight">
                Anstehende Reifenwechsel ({tireReminders.length})
              </h2>
            </div>
            <Link to="/maintenance" className="text-xs text-[#525866] hover:text-[#7C3AED]">Alle ansehen</Link>
          </div>
          <div className="divide-y divide-[#E2E4E9]">
            {tireReminders.slice(0, 6).map((t) => (
              <Link
                key={t.vehicle_id}
                to={`/vehicles/${t.vehicle_id}`}
                className={`flex items-center gap-3 p-3 hover:bg-[#FAFAFB] ${t.is_overdue ? 'bg-[#FFF5F5]' : ''}`}
                data-testid={`tire-reminder-dash-${t.vehicle_id}`}
              >
                {t.is_overdue
                  ? <Warning weight="fill" size={16} className="text-[#E60000] shrink-0" />
                  : <Calendar weight="bold" size={16} className="text-[#7C3AED] shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#111418] truncate">
                    {t.brand} {t.model}
                    <span className="ml-2 text-xs text-[#525866] font-mono">{t.license_plate}</span>
                  </p>
                  <p className="text-xs text-[#525866]">
                    Wechsel auf {{summer:'Sommerreifen',winter:'Winterreifen'}[t.target_type]}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className={`text-xs font-semibold ${t.is_overdue ? 'text-[#E60000]' : (t.days_until <= 7 ? 'text-[#FFB800]' : 'text-[#525866]')}`}>
                    {t.is_overdue ? `${Math.abs(t.days_until)} Tage überfällig` : `in ${t.days_until} Tagen`}
                  </p>
                  <p className="text-xs text-[#8A94A6]">{new Date(t.target_date).toLocaleDateString('de-DE')}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Quick links */}
      <div className="card-swiss p-4">
        <p className="overline mb-3">Schnellzugriff</p>
        <div className="flex flex-wrap gap-3">
          <Link to="/vehicles" className="btn-outline px-4 py-2 text-sm flex items-center gap-2">
            <Car weight="bold" size={16} /> Fahrzeuge
          </Link>
          <Link to="/customers" className="btn-outline px-4 py-2 text-sm flex items-center gap-2">
            <Users weight="bold" size={16} /> Kunden
          </Link>
          <Link to="/calendar" className="btn-outline px-4 py-2 text-sm flex items-center gap-2">
            <Calendar weight="bold" size={16} /> Kalender
          </Link>
          <Link to="/invoices" className="btn-outline px-4 py-2 text-sm flex items-center gap-2">
            <CurrencyEur weight="bold" size={16} /> Finanzen
          </Link>
          <Link to="/quotes" className="btn-outline px-4 py-2 text-sm flex items-center gap-2">
            <ArrowRight weight="bold" size={16} /> Angebote
          </Link>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="card-swiss">
        <div className="p-4 border-b border-[#E2E4E9] flex items-center justify-between">
          <h2 className="text-lg font-bold tracking-tight">Aktuelle Buchungen</h2>
          <Link to="/bookings" className="text-sm text-[#E60000] hover:underline flex items-center gap-1">
            Alle anzeigen
            <ArrowRight weight="bold" size={14} />
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="table-swiss">
            <thead>
              <tr>
                <th>Kunde</th>
                <th>Fahrzeug</th>
                <th>Zeitraum</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recentBookings.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-[#8A94A6]">
                    Keine Buchungen vorhanden
                  </td>
                </tr>
              ) : (
                recentBookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-[#F7F8FA]">
                    <td>
                      <p className="font-medium text-[#111418]">
                        {booking.customer?.first_name} {booking.customer?.last_name}
                      </p>
                    </td>
                    <td>
                      <p className="text-[#525866]">
                        {booking.vehicle?.brand} {booking.vehicle?.model}
                      </p>
                      <p className="text-xs text-[#8A94A6]">{booking.vehicle?.license_plate}</p>
                    </td>
                    <td>
                      <p className="text-sm">
                        {new Date(booking.start_date).toLocaleDateString('de-DE')} - {new Date(booking.end_date).toLocaleDateString('de-DE')}
                      </p>
                    </td>
                    <td>{getStatusBadge(booking.status)}</td>
                    <td>
                      <Link 
                        to={`/bookings/${booking.id}`}
                        className="text-[#E60000] hover:underline text-sm"
                      >
                        Details
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
